import { i18n } from '@/lib/i18n/i18n';
import type { ErrorResponse, ReadOnlyReason } from '@/types/generated';

export interface InsufficientCreditsRefusal {
  code: 'insufficient_credits';
  detail: string;
  required: number;
  available: number;
  renewsAt: string | null;
}

export interface ReadOnlyRefusal {
  code: 'read_only';
  detail: string;
  reason: ReadOnlyReason | null;
}

export type BillingRefusal = InsufficientCreditsRefusal | ReadOnlyRefusal;

const finiteNumber = (value: unknown, fallback: number): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback;

// A missing or malformed member degrades to a safe default rather than throwing.
export const billingRefusalFromProblem = (
  status: number,
  body: Partial<ErrorResponse> | undefined | null,
): BillingRefusal | null => {
  if (status !== 402 || !body) return null;

  const detail =
    body.detail ?? i18n.t('billing_refusal.default_detail', { ns: 'billing' });
  const context = body.context ?? {};

  if (body.code === 'insufficient_credits') {
    return {
      code: 'insufficient_credits',
      detail,
      required: finiteNumber(context.required, 0),
      available: finiteNumber(context.available, 0),
      renewsAt:
        typeof context.renews_at === 'string' ? context.renews_at : null,
    };
  }

  if (body.code === 'read_only') {
    // The server owns this vocabulary; an unknown member falls through to null.
    return {
      code: 'read_only',
      detail,
      reason:
        typeof context.reason === 'string'
          ? (context.reason as ReadOnlyReason)
          : null,
    };
  }

  return null;
};

// A stream frame names the refusal without its numbers; the dialog reads only code + detail.
export const refusalFromCode = (
  code: BillingRefusal['code'],
  detail: string,
  reason: ReadOnlyReason | null = null,
): BillingRefusal =>
  code === 'read_only'
    ? { code, detail, reason }
    : { code, detail, required: 0, available: 0, renewsAt: null };
